import { HttpService } from '@skylab-shared/core';
import { AggregatedEmailStatusCode, EmailDeliveryStatus } from '@skylab-shared/dataModel';
import { fillPlaceholders } from '@skylab-shared/utils';
import { Type } from 'class-transformer';
import { EventNotification, EventNotificationType, EventNotificationsServiceBase } from '../../event-notifications.service-base';
import { NotificatorBase } from '../notificator-base';

export type EmailStatusEventData = {
    readonly kind: string;
    readonly id: string;
};

export type EmailStatus = EmailStatusDTO;

class EmailStatusDTO {
    public readonly id: number;
    public readonly aggregatedEmailStatusCode: AggregatedEmailStatusCode;
    @Type(() => EmailDeliveryStatus) public readonly emailStatuses: EmailDeliveryStatus[];
}

export abstract class EmailStatusesNotificatorBase<TRequestData extends EmailStatusEventData = EmailStatusEventData> extends NotificatorBase<TRequestData, EmailStatus[]> {
    protected eventType: EventNotificationType = 'EmailStatusUpdated';

    public constructor(
        eventNotifications: EventNotificationsServiceBase,
        http: HttpService
    ) {
        super(eventNotifications, http);
    }

    protected abstract url: string;
    protected abstract kind: string;

    protected getEmailStatusesAsync(ids: number[]): Promise<EmailStatus[]> {
        const requests = ids.map(id => this.getEmailStatusRequest(id));
        return Promise.all(requests);
    }

    protected override filterEvents(events: EventNotification<TRequestData>[]): EventNotification<TRequestData>[] {
        return super.filterEvents(events)
            .filter(elem => elem.properties!.kind === this.kind);
    }

    protected prepareDataAsync(events: EventNotification<EmailStatusEventData>[]): Promise<EmailStatus[]> {
        const ids = events.map(elem => +elem.properties!.id);
        return this.getEmailStatusesAsync(ids);
    }

    private async getEmailStatusRequest(id: number): Promise<EmailStatus> {
        const url = fillPlaceholders(this.url, { id: id });
        const { data } = await this.http.get(url, {dto: EmailStatusDTO});
        return { ...data, id: data.id ?? id };
    }
}
